import type { BrandKit } from "@redotech/redo-content-builder-common/brand-kit";
import type { OrderLookupSettings } from "@redotech/redo-order-tracking-lookup-common/order-lookup-defaults";
import type { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";

export interface OrderLookupConfig {
  orderLookupSettings?: OrderLookupSettings | null;
  brandKit?: BrandKit | null;
  widgetSlug?: string | null;
  loadingBackgroundColor?: string | null;
}

/**
 * Source the order-tracking login form's config from the (CDN + localStorage
 * SWR) cached autocheck payload so the form paints correctly-branded on first
 * render, without the uncached getTrackingPageForStorefront RPC whose Mongo p99
 * tail blanked the form.
 *
 * Falls back to the RPC only when the cached autocheck predates these fields
 * (older blob during rollout) or the fetch fails, so existing caches keep
 * working. The autocheck is read once here; its SWR background refresh only
 * updates the next page load, so the rendered form never swaps under the user.
 */
export async function resolveOrderLookupConfig({
  getAutocheck,
  fetchViaRpc,
  onFallback,
}: {
  getAutocheck: () => Promise<WidgetAutocheck>;
  fetchViaRpc: () => Promise<OrderLookupConfig>;
  onFallback?: (error?: unknown) => void;
}): Promise<OrderLookupConfig> {
  try {
    const autocheck = await getAutocheck();
    const orderTrackingLogin = autocheck.orderTrackingLogin;
    if (orderTrackingLogin && "settings" in orderTrackingLogin) {
      return {
        orderLookupSettings: orderTrackingLogin.settings ?? null,
        brandKit: autocheck.brandKit,
        widgetSlug: autocheck.widgetSlug,
        loadingBackgroundColor:
          orderTrackingLogin.loadingBackgroundColor ?? undefined,
      };
    }
    onFallback?.();
  } catch (error) {
    onFallback?.(error);
  }
  return await fetchViaRpc();
}
